import {createApi} from '@reduxjs/toolkit/query/react';

import {isNil, isNotNil} from 'ramda';

import {toFinancialProductPreferenceRequestBody} from 'features/financial-products';

import {getOnlineOfferDataSearch} from '../utils/getOnlineOfferDataSearch';
import {getPdfQueryParams} from '../utils/getPdfQueryParams';
import {dmsBaseQuery} from './dmsBaseQuery';
import {reportFinancialProductQuoteMismatch} from './reportFinancialProductQuoteMismatch';
import {
  ChooseOnlineOfferAlternativeApiArg,
  GetOnlineOfferAlternativesApiArg,
  OnlineOfferAlternativesResponseBody,
} from './types/onlineOfferAlternativesTypes';
import {
  GetBusinessCaseOnlineOfferApiArg,
  GetBusinessCaseOnlineOfferApiResponse,
  GetOnlineOfferFinancialProductsApiArg,
  GetOnlineOfferFinancialProductsApiResponse,
  GetPdfByOfferIdApiArg,
  GetPdfByOfferIdApiResponse,
  ListLogosApiArg,
  ListLogosApiResponse,
  PublicFinancialProductsResponseBodySchema,
  RefuseOnlineOfferCommunicationApiArg,
  SendOnlineOfferEngagementApiArg,
  SubmitOnlineOfferRatingApiArg,
  UpdateFinancialProductPreferenceApiArg,
  UpdateFinancialProductPreferenceApiResponse,
} from './types/onlineOfferTypes';

// See PublicFinancialProductsResponseBodySchema for the public-payload privacy invariant.
const toPublicFinancialProducts = (body: unknown) =>
  PublicFinancialProductsResponseBodySchema.strip().parse(body);

const ONLINE_OFFER_TAG = 'OnlineOffer';

/** A 409 from the rating endpoint means the purchase is already rated. */
export const isRatingAlreadyRecordedError = (error: unknown): boolean =>
  isNotNil(error) && typeof error === 'object' && 'status' in error && error.status === 409;

const toPriceKey = (price: {amount: string; currency: string} | null | undefined) =>
  price ? `${price.amount} ${price.currency}` : null;

export const onlineOfferApi = createApi({
  baseQuery: dmsBaseQuery,
  reducerPath: 'onlineOfferApi',
  tagTypes: [ONLINE_OFFER_TAG, 'OnlineOfferAlternatives'],
  endpoints: (build) => ({
    submitOnlineOfferRating: build.mutation<void, SubmitOnlineOfferRatingApiArg>({
      query: ({offerId, workspace, rating, recommendation, comment}) => ({
        url: `/dms/v1/business-case/online-offer/${encodeURIComponent(offerId)}/rating`,
        method: 'POST',
        headers: {'X-Workspace': workspace},
        body: {rating, recommendation, comment},
      }),
      // A conflict also refetches the rating already recorded for this purchase.
      // Other failures must not refetch: the loading state remounts the form and loses input (T20-142729).
      invalidatesTags: (_result, error) =>
        isNil(error) || isRatingAlreadyRecordedError(error) ? [ONLINE_OFFER_TAG] : [],
    }),
    refuseOnlineOfferCommunication: build.mutation<void, RefuseOnlineOfferCommunicationApiArg>({
      query: ({offerId, workspace}) => ({
        url: `/dms/v1/business-case/online-offer/${encodeURIComponent(offerId)}/communication-refusal`,
        method: 'POST',
        headers: {'X-Workspace': workspace},
      }),
    }),
    getBusinessCaseOnlineOffer: build.query<
      GetBusinessCaseOnlineOfferApiResponse,
      GetBusinessCaseOnlineOfferApiArg
    >({
      query: (queryArg) => {
        const paramsToSet = getOnlineOfferDataSearch(queryArg);

        return {
          url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/data${paramsToSet ? `?${paramsToSet}` : ''}`,
          headers: {'X-Workspace': queryArg.workspace},
        };
      },
      providesTags: [ONLINE_OFFER_TAG],
    }),
    /** Products are polled separately because current configuration and price can invalidate them. */
    getOnlineOfferFinancialProducts: build.query<
      GetOnlineOfferFinancialProductsApiResponse,
      GetOnlineOfferFinancialProductsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/financial-products`,
        headers: {
          'X-Workspace': queryArg.workspace,
          'Accept-Language': queryArg.language,
        },
      }),
      transformResponse: toPublicFinancialProducts,
      /**
       * Re-read `/data` once when the polled pricing basis changes; in-flight reads remain fresher.
       */
      async onQueryStarted(arg, {dispatch, getState, getCacheEntry, queryFulfilled}) {
        const previouslyPolledPrice = toPriceKey(getCacheEntry().data?.vehiclePriceWithVat);

        try {
          const {data} = await queryFulfilled;
          reportFinancialProductQuoteMismatch(arg.offerId, data);
          const polledPrice = toPriceKey(data.vehiclePriceWithVat);

          if (polledPrice === previouslyPolledPrice) {
            return;
          }

          // Preserve the session and opener carried by the cached `/data` request.
          const loadedOffer = onlineOfferApi.util
            .selectCachedArgsForQuery(getState(), 'getBusinessCaseOnlineOffer')
            .map((originalArgs) =>
              onlineOfferApi.endpoints.getBusinessCaseOnlineOffer.select(originalArgs)(getState())
            )
            .find((read) => read.originalArgs?.offerId === arg.offerId);

          if (
            loadedOffer?.status !== 'fulfilled' ||
            polledPrice === toPriceKey(loadedOffer.data?.vehiclePriceWithVat)
          ) {
            return;
          }

          dispatch(onlineOfferApi.util.invalidateTags([ONLINE_OFFER_TAG]));
        } catch {
          // A failed poll says nothing about the price; the surface reports it.
        }
      },
    }),
    /**
     * The full priced response is the quote, so the frontend never assembles its own.
     * A stale write response must not replace a frozen snapshot or later reopened read.
     */
    updateFinancialProductPreference: build.mutation<
      UpdateFinancialProductPreferenceApiResponse,
      UpdateFinancialProductPreferenceApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/financial-product-preference`,
        method: 'PUT',
        headers: {
          'X-Workspace': queryArg.workspace,
          'Accept-Language': queryArg.language,
        },
        body: toFinancialProductPreferenceRequestBody(
          queryArg.coordinates,
          queryArg.expectedPreferenceRevision
        ),
      }),
      transformResponse: toPublicFinancialProducts,
      onQueryStarted(arg, {queryFulfilled}) {
        queryFulfilled
          .then(({data}) => reportFinancialProductQuoteMismatch(arg.offerId, data))
          // The writer handles a refusal; the next poll checks a 409's `current` body.
          .catch(() => undefined);
      },
    }),
    getOnlineOfferAlternatives: build.query<
      OnlineOfferAlternativesResponseBody,
      GetOnlineOfferAlternativesApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/alternatives`,
        headers: {'X-Workspace': queryArg.workspace, 'Accept-Language': queryArg.language},
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'OnlineOfferAlternatives', id: queryArg.offerId},
      ],
    }),
    chooseOnlineOfferAlternative: build.mutation<void, ChooseOnlineOfferAlternativeApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/alternative/${queryArg.alternativeId}/choice`,
        method: 'POST',
        headers: {'X-Workspace': queryArg.workspace},
        body: {revision: queryArg.revision},
      }),
      // A 409 means the alternative changed under the customer: refetch either way.
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'OnlineOfferAlternatives', id: queryArg.offerId},
      ],
    }),
    listLogos: build.query<ListLogosApiResponse, ListLogosApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/logo`,
        headers: {'X-Workspace': queryArg['X-Workspace']},
      }),
    }),
    getPdfByOfferId: build.query<GetPdfByOfferIdApiResponse, GetPdfByOfferIdApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/pdf`,
        params: getPdfQueryParams(queryArg),
        headers: {
          'X-Workspace': queryArg.workspace,
        },
      }),
    }),
    sendOnlineOfferEngagement: build.mutation<void, SendOnlineOfferEngagementApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/online-offer/${queryArg.offerId}/engagement`,
        method: 'POST',
        headers: {'X-Workspace': queryArg.workspace},
        body: {
          sessionId: queryArg.sessionId,
          activeTimeMs: queryArg.activeTimeMs,
          scrollDepth: queryArg.scrollDepth,
          certificateClick: queryArg.certificateClick,
        },
      }),
    }),
  }),
});

export const {
  useSubmitOnlineOfferRatingMutation,
  useRefuseOnlineOfferCommunicationMutation,
  useGetBusinessCaseOnlineOfferQuery,
  useGetOnlineOfferAlternativesQuery,
  useChooseOnlineOfferAlternativeMutation,
  useGetOnlineOfferFinancialProductsQuery,
  useUpdateFinancialProductPreferenceMutation,
  useListLogosQuery,
  useLazyGetPdfByOfferIdQuery,
  useSendOnlineOfferEngagementMutation,
} = onlineOfferApi;
